import {assert} from 'Asserts';
import lazyInit from 'util/lazyInit';

import APIIDPrefixToTypename from '../../graphql-fragments/APIIDPrefixToTypename.json5';

export type ApiIdPrefixToTypenameMapping = Record<string, string>;

interface APIIDPrefixToTypename {
  mapping: Array<{
    apiIdPrefix: string;
    __typename: string;
  }>;
}

export const TYPENAME_MAPPING_FILENAME = 'APIIDPrefixToTypename.json5';

/**
 * Extra apiIdPrefix -> __typename candidates for prefixes that are genuinely ambiguous, i.e. shared
 * by more than one GraphQL type, and so can't be added to APIIDPrefixToTypename.json5 (see the
 * comment at the top of that file).
 *
 * ComplexPolymerNode, EquipmentNode, OligoConjugateNode, and OligoDuplexNode are Static Schema
 * Interface projections over the generic custom entity (BasicFolderItemNode) and share its 'bfi'
 * id prefix, so an id alone can't tell you which of the two you have.
 *
 * Code that needs to consider every possible type behind an ambiguous-prefix id (e.g. trying every
 * candidate cache key) should combine this with getPrefixToTypename(); see
 * getAllPossibleTypenamesForPrefix.
 */
export const AMBIGUOUS_PREFIX_EXTRA_TYPENAMES: ReadonlyArray<{apiIdPrefix: string; __typename: string}> = [
  {apiIdPrefix: 'bfi', __typename: 'ComplexPolymerNode'},
  {apiIdPrefix: 'bfi', __typename: 'EquipmentNode'},
  {apiIdPrefix: 'bfi', __typename: 'OligoConjugateNode'},
  {apiIdPrefix: 'bfi', __typename: 'OligoDuplexNode'},
  // AA sequences are reachable under two typenames while BNCH-169122's rename is in flight, and
  // which one the server emits changes at the canonical flip. Listing the replacement here means
  // `getAllPossibleTypenamesForPrefix` returns both, so cache maintenance that iterates every
  // candidate, `syncItemNameToApolloCache` in particular, updates whichever entry exists rather
  // than silently no-op'ing on the other. Removed once the old names are gone.
  {apiIdPrefix: 'prtn', __typename: 'AaSequenceNode'},
  {apiIdPrefix: 'prtnalnmnt', __typename: 'AaSequenceAlignmentNode'},
];

/**
 * Returns every GraphQL __typename that an id with the given apiIdPrefix could possibly be,
 * combining the unambiguous mapping in APIIDPrefixToTypename.json5 with the known ambiguous-prefix
 * candidates above. Returns an empty array if the prefix isn't recognized at all.
 */
export function getAllPossibleTypenamesForPrefix(apiIdPrefix: string): Array<string> {
  const primary = getPrefixToTypename()[apiIdPrefix];
  const extras = AMBIGUOUS_PREFIX_EXTRA_TYPENAMES.filter(
    (mapping) => mapping.apiIdPrefix === apiIdPrefix
  ).map((mapping) => mapping.__typename);
  return primary != null ? [primary, ...extras] : extras;
}

const productionGetPrefixToTypename = lazyInit(() => {
  const mapping = (APIIDPrefixToTypename as APIIDPrefixToTypename).mapping;
  const prefixToTypename: ApiIdPrefixToTypenameMapping = {};
  mapping.forEach(({apiIdPrefix, __typename}) => {
    prefixToTypename[apiIdPrefix] = __typename;
  });
  assert(
    Object.keys(prefixToTypename).length === mapping.length,
    `Duplicate API ID Prefix in ${TYPENAME_MAPPING_FILENAME}`
  );
  return prefixToTypename;
});

// eslint-disable-next-line import-x/no-mutable-exports
export let getPrefixToTypename = productionGetPrefixToTypename;

/** @package */
export function overridePrefixToTypenameForTests(extraForTests: ApiIdPrefixToTypenameMapping): void {
  getPrefixToTypename = () => ({...productionGetPrefixToTypename(), ...extraForTests});
}

/** @package */
export function restorePrefixToTypenameForTests(): void {
  getPrefixToTypename = productionGetPrefixToTypename;
}
